import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtDate, fmtNum } from "../lib/api";
import { PageHeader, Stat, StatusBadge, Section } from "../components/common";
import { Button } from "../components/ui/button";
import { AlertTriangle, ArrowRight, CheckCircle2, Eye, MousePointerClick, MessageSquare, UserX, MailX } from "lucide-react";

const EVT = {
  open: [Eye, "a ouvert", "text-emerald-700 bg-emerald-50"], click: [MousePointerClick, "a cliqué un lien", "text-[#0b1f3a] bg-slate-100"],
  reply: [MessageSquare, "a répondu", "text-blue-700 bg-blue-50"], unsubscribe: [UserX, "s'est désinscrit", "text-red-700 bg-red-50"],
  bounce: [MailX, "rebond (adresse invalide)", "text-amber-800 bg-amber-50"],
};

function ActivityFeed() {
  const [ev, setEv] = useState(null);
  useEffect(() => {
    const load = () => api.get("/activity").then((r) => setEv(r.data));
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);
  return (
    <Section title="Activité en temps réel" description="20 derniers événements · actualisé toutes les 15 s">
      {!ev ? <div className="text-sm text-slate-500">Chargement…</div> : ev.length === 0 ? <div className="text-sm text-slate-500">Aucune activité pour le moment.</div> : (
        <div className="divide-y divide-slate-100 -my-3" data-testid="activity-feed">
          {ev.map((e, i) => {
            const [Icon, label, cls] = EVT[e.type];
            const inner = (
              <div className="py-2.5 flex items-center gap-3 fade-up" style={{ animationDelay: `${i * 25}ms` }} data-testid={`activity-item-${i}`}>
                <span className={`h-7 w-7 rounded-full flex items-center justify-center shrink-0 ${cls}`}><Icon className="h-3.5 w-3.5" /></span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm truncate"><span className="font-medium">{e.contact_name}</span> {label}{e.company_name && <span className="text-slate-400"> · {e.company_name}</span>}</div>
                  <div className="text-xs text-slate-400 truncate">{e.campaign_name}</div>
                </div>
                <span className="text-xs text-slate-400 shrink-0">{fmtDate(e.at)}</span>
              </div>
            );
            return e.reply_id ? <Link key={i} to="/inbox" className="block hover:bg-slate-50">{inner}</Link>
              : e.campaign_id ? <Link key={i} to={`/campaigns/${e.campaign_id}`} className="block hover:bg-slate-50">{inner}</Link> : <div key={i}>{inner}</div>;
          })}
        </div>
      )}
    </Section>
  );
}

const STEPS = [
  ["Importer un CSV", "/companies"], ["Lancer l'agent de recherche", "/companies"], ["Vérifier les contacts", "/contacts"],
  ["Préparer une campagne", "/campaigns"], ["Valider le lancement", "/campaigns"], ["Suivre les réponses", "/inbox"],
];

export default function Dashboard() {
  const [d, setD] = useState(null);
  useEffect(() => {
    const load = () => api.get("/dashboard").then((r) => setD(r.data));
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, []);
  if (!d) return <div className="text-sm text-slate-500">Chargement…</div>;
  const stats = [
    ["Entreprises importées", fmtNum(d.companies), "stat-companies"], ["Contacts identifiés", fmtNum(d.contacts), "stat-contacts"],
    ["Emails vérifiés", fmtNum(d.verified), "stat-verified"], ["Envoyés aujourd'hui", `${d.sent_today} / ${d.daily_cap}`, "stat-sent-today"],
    ["Envoyés · 7 jours", fmtNum(d.sent_7d), "stat-sent-7d"], ["Envoyés · 30 jours", fmtNum(d.sent_30d), "stat-sent-30d"],
    ["Réponses reçues", fmtNum(d.replies), "stat-replies"], ["Taux de réponse", `${d.reply_rate} %`, "stat-reply-rate"],
    ["Rebonds", fmtNum(d.bounces), "stat-bounces"], ["Désinscriptions", fmtNum(d.unsubscribes), "stat-unsubscribes"],
    ["Campagnes actives", fmtNum(d.active_campaigns), "stat-active-campaigns"],
  ];
  return (
    <div>
      <PageHeader eyebrow="Vue d'ensemble" title="Tableau de bord" description="Indicateurs consolidés de la prospection. Les ouvertures sont des estimations ; les clics et réponses sont fiables." />
      {d.global_pause && (
        <div className="mb-6 flex items-start gap-3 border border-red-200 bg-red-50 text-red-800 rounded-md px-4 py-3 text-sm" data-testid="global-pause-banner">
          <AlertTriangle className="h-4 w-4 mt-0.5" />
          <div>Tous les envois sont suspendus : {d.global_pause_reason || "arrêt manuel"}. <Link className="underline" to="/settings">Gérer dans Paramètres</Link></div>
        </div>
      )}
      {!d.microsoft?.connected && (
        <div className="mb-6 flex items-center justify-between border border-amber-200 bg-amber-50 text-amber-900 rounded-md px-4 py-3 text-sm" data-testid="ms-not-connected-banner">
          <span>Microsoft 365 n'est pas connecté : aucun email ne peut être envoyé ni aucune réponse détectée.</span>
          <Link to="/settings"><Button size="sm" variant="outline">Connecter</Button></Link>
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 mb-10">
        {stats.map(([l, v, id], i) => <div key={id} className="fade-up" style={{ animationDelay: `${i * 30}ms` }}><Stat label={l} value={v} testid={id} /></div>)}
      </div>
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <ActivityFeed />
          <Section title="Dernières réponses" actions={<Link to="/inbox" className="text-xs text-slate-600 hover:text-[#0b1f3a] flex items-center gap-1">Tout voir <ArrowRight className="h-3 w-3" /></Link>}>
            {d.recent_replies.length === 0 ? <div className="text-sm text-slate-500">Aucune réponse pour le moment.</div> : (
              <div className="divide-y divide-slate-100 -my-3" data-testid="recent-replies">
                {d.recent_replies.map((r) => (
                  <div key={r.id} className="py-3 flex items-center justify-between gap-4">
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{r.contact_name || r.from_email} <span className="text-slate-400 font-normal">· {r.company_name}</span></div>
                      <div className="text-xs text-slate-500 truncate">{r.summary || r.subject}</div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0"><StatusBadge status={r.category} /><span className="text-xs text-slate-400">{fmtDate(r.received_at)}</span></div>
                  </div>
                ))}
              </div>
            )}
          </Section>
          <Section title="Alertes" description="Anomalies de délivrabilité, quotas, blocages fournisseurs">
            {d.alerts.length === 0 ? <div className="text-sm text-slate-500 flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600" />Aucune alerte non lue.</div> : (
              <div className="space-y-2" data-testid="alerts-list">
                {d.alerts.map((a) => (
                  <div key={a.id} className={`text-sm rounded px-3 py-2 border ${a.level === "critical" || a.level === "error" ? "bg-red-50 border-red-200 text-red-800" : "bg-amber-50 border-amber-200 text-amber-900"}`}>
                    <span className="text-xs opacity-70 mr-2">{fmtDate(a.created_at)}</span>{a.message}
                  </div>
                ))}
              </div>
            )}
          </Section>
        </div>
        <Section title="Parcours" description="Les 10 étapes, résumées">
          <ol className="space-y-3">
            {STEPS.map(([s, to], i) => (
              <li key={s}><Link to={to} className="flex items-center gap-3 text-sm group">
                <span className="tabular text-xs text-slate-400 w-5">{String(i + 1).padStart(2, "0")}</span>
                <span className="group-hover:underline">{s}</span>
              </Link></li>
            ))}
          </ol>
        </Section>
      </div>
    </div>
  );
}
